const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/DashboardPage/Components/appointmentCards/AppointmentDetails.tsx';
import _ from 'lodash';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import textMap from './translations';
import { Gap, GapSm } from 'App/components/Gap/Gap';
import { LinkHrefTagFrame } from 'App/components/old_components/components/LinkFrame';
import { getIsSurgeryAppointmentDeadlinePassed, getTextStuff, TextStuff } from './utils';
import {
    appointmentCardSubtitleStyle,
    flexRow,
    fullWidth,
    geoCellStyle,
    gridStyle,
    labelCellStyle,
    styleIcon,
    textCellStyle,
} from './styles';
import { AttachmentList } from 'App/components/AttachmentList/AttachmentList';
import { useTheme } from '@mui/material/styles';
import { AppointmentConfirmButton } from '../../../appointment/components/AppointmentConfirmButton';
import { StructuredAppointment } from '@cistec/common_web/defs/kisim';
import { css, cx } from '@emotion/css';
import { useFeatureFlags } from 'App/store/featureFlags';
import { useAppointmentAttachments } from 'App/store/appointmentAttachment';
import { Alert } from 'App/components/Alert/Alert';
import { getConfiguration } from 'App/utils/clientSetupGetRessource';
import { Fragment, ReactNode, useMemo } from 'react';
import { TextInput, useText } from '@cistec/common_web/utils/intlUtils';
import { getAppointmentObjectTitle } from 'App/utils/appointment';
import { convertStructuredAppointmentToAppointmentObject } from '@cistec/common_web/utils/kisim';
import { useSelector } from 'react-redux';
import { selectEmailFromRegisterWorkItem } from 'App/store/login';
import { useDurationText } from '@cistec/common_web/utils/dateTime/dateTime';
import { TelemedicineBtn } from 'App/components/Buttons/TelemedicineBtn';
import { SublimdBtn } from 'App/components/Buttons/SublimdButton';
import { getAppointmentStatusChip } from 'App/components/utils';

interface Props {
    title: TextInput;
    hideAppointmentConfirmButton?: boolean;
    hideAppointmentTitle?: boolean;
    hideAppointmentName?: boolean;
    hideStatus?: boolean;
    appointment: StructuredAppointment;
}

export const getDetailRow = (label: string, text: ReactNode) => {
    if (!text) return null;

    return (
        <Fragment key={label}>
            <div className={labelCellStyle} key={label}>
                {label}
            </div>
            <div className={textCellStyle} key={`${label}_text`}>
                {text}
            </div>
        </Fragment>
    );
};

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            titleRowCSS: css`
                font-weight: bold;
                grid-column-start: span 2;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                flex-wrap: nowrap;

                ${theme.breakpoints.down('sm')} {
                    flex-direction: column;
                    flex-wrap: wrap;
                    align-items: flex-start;

                    > button {
                        margin: ${theme.spacing(1)} 0;
                    }
                }
            `,
            appointmentConfirmationButtonWrapperCss: css`
                display: flex;
                justify-content: end;
                margin-bottom: ${theme.spacing(2)};
            `,
        }),
        [theme]
    );
}

export const AppointmentDetails = (props: Props): any => {
    const { formatMessage } = useIntl();

    const { data: featureFlags } = useFeatureFlags();
    const { appointmentConfirmationButtonWrapperCss, titleRowCSS } = useCSSClasses();
    const { surgeryCardHint, SurgeryCardContactAlert } = getConfiguration();

    const { appointment } = props;
    const appointmentObject = convertStructuredAppointmentToAppointmentObject(appointment, formatMessage);
    const title = useText(props.title);
    const durationText = useDurationText(appointment.STARTDAT, appointment.STOPDAT);

    const { data: attachments } = useAppointmentAttachments(appointment.TERMNR);
    const textStuff: TextStuff = getTextStuff(appointment, formatMessage);

    const { klinikText, resName, termStartText, termTxt, zustArzt } = textStuff;
    const practitioner = zustArzt || resName;
    const showPractitioner = featureFlags.appointment.showResponsibilities && !!practitioner;

    const ressourcenStandort = appointment.RESSOURCENSTANDORT;
    const standort = ressourcenStandort?.Standort;
    const address = ressourcenStandort?.Adresse;
    const phone = ressourcenStandort?.TelefonStandort;
    const externalLink = appointment.EXTERNALLINK;

    const currentPatEmail = useSelector(selectEmailFromRegisterWorkItem);

    const getAppointmentCardTitleRow = (title: ReactNode) => (
        <div className={flexRow} key="appointmentTitleRow">
            <Typography component="h2" sx={{ color: 'primary.main' }} variant="subtitle1">
                {title}
            </Typography>
        </div>
    );

    const getAddressCardDetails = () => {
        if (!(address || phone)) return null;

        return (
            <Fragment key={'address'}>
                {address && (
                    <LinkHrefTagFrame
                        className={geoCellStyle}
                        styleIcon={styleIcon}
                        text={address}
                        href={`https://maps.google.com/?q=${address}`}
                    />
                )}
                {phone && (
                    <LinkHrefTagFrame
                        className={geoCellStyle}
                        styleIcon={styleIcon}
                        text={phone}
                        href={`tel:${phone.replace(' ', '')}`}
                        iconName="icons8-phone"
                    />
                )}
            </Fragment>
        );
    };

    const getTitleRow = () => {
        const name = getAppointmentObjectTitle(appointmentObject, formatMessage, currentPatEmail);

        return (
            <div key="titleRow" className={titleRowCSS}>
                {!props.hideAppointmentName && <div>{name}</div>}
                <TelemedicineBtn externalLink={externalLink} />
                {appointment.SUBLIMDSTATE === 'READY' ? <SublimdBtn termNr={appointment.TERMNR} /> : null}
            </div>
        );
    };

    const getDurationRow = () => {
        if (appointment.TYP !== 'KONS') return null;
        return getDetailRow(formatMessage(textMap.duration), durationText);
    };

    const getStatusRow = () => {
        if (props.hideStatus) return null;

        return getDetailRow(formatMessage(textMap.proc1Label), getAppointmentStatusChip(appointment, formatMessage));
    };

    const detailRows = _.compact([
        getTitleRow(),
        getStatusRow(),
        appointment.EINTRITTDAT_PATIENT === undefined && appointment.TYP === 'OPERATION'
            ? getDetailRow(formatMessage(textMap.termStartLabel), formatMessage(textMap.dateToBeDefined))
            : getDetailRow(formatMessage(textMap.termStartLabel), termStartText),
        getDurationRow(),
        showPractitioner ? getDetailRow(formatMessage(textMap.zustArztLabel), practitioner) : null,
        getDetailRow(formatMessage(textMap.standortLabel), standort),
        getAddressCardDetails(),
        getDetailRow(formatMessage(textMap.clinikLabel), klinikText),
    ]);

    const getAttachmentList = () => (attachments?.length ? <AttachmentList attachments={attachments} /> : null);

    const getConfirmAppointmentButtonRow = () => {
        return (
            <div className={cx(fullWidth, appointmentConfirmationButtonWrapperCss)} key="confirmAppointmentButtonRow">
                <AppointmentConfirmButton appointment={appointment} />
                <Gap />
            </div>
        );
    };

    const isOperation = appointment.TYP === 'OPERATION';

    return (
        <Fragment key={'Appointment'}>
            {getAppointmentCardTitleRow(title)}
            <Grid size={{ xs: 12 }}>
                {!props.hideAppointmentTitle && (
                    <Typography variant="subtitle2" component="h2" className={appointmentCardSubtitleStyle}>
                        {termTxt}
                    </Typography>
                )}
                <div className={gridStyle}>{detailRows}</div>
                {featureFlags.appointment.appointmentDocs ? getAttachmentList() : null}
                <GapSm />
            </Grid>
            {isOperation && SurgeryCardContactAlert && (
                <>
                    <Alert color="info" title={formatMessage(SurgeryCardContactAlert.title)}>
                        {formatMessage(SurgeryCardContactAlert.text)}
                    </Alert>
                    <Gap />
                </>
            )}
            {getIsSurgeryAppointmentDeadlinePassed(appointment) && appointment.EINTRITTDAT_PATIENT && (
                <>
                    <Alert color="caution" title={formatMessage(surgeryCardHint?.title || textMap.surgeryHintTitle)}>
                        {formatMessage(surgeryCardHint?.text || textMap.surgeryHintText, { br: <br /> })}
                    </Alert>
                    <Gap />
                </>
            )}
            {!props.hideAppointmentConfirmButton && getConfirmAppointmentButtonRow()}
        </Fragment>
    );
};
